import * as React from "react";
import type { 
  CityPerformanceTableProps, 
  CityData, 
  GeographicTableColumn, 
  TableSortConfig, 
  TableFilterConfig 
} from '../../types/geographic';

// Table columns configuration
const COLUMNS: GeographicTableColumn[] = [
  { key: 'rank', label: 'Rank', sortable: true, width: '80px' },
  { key: 'city_name', label: 'City', sortable: true },
  { key: 'state_name', label: 'State', sortable: true, width: '120px' },
  { key: 'user_count', label: 'Total Users', sortable: true, format: 'number' },
  { key: 'client_count', label: 'Clients', sortable: true, format: 'number' },
  { key: 'professional_count', label: 'Professionals', sortable: true, format: 'number' },
  { key: 'revenue', label: 'Revenue', sortable: true, format: 'currency' },
  { key: 'roi', label: 'ROI', sortable: true, format: 'percentage' },
  { key: 'population', label: 'Population', sortable: true, format: 'number' }
];

// Utility functions
const formatValue = (value: any, format?: GeographicTableColumn['format']): string => {
  if (value == null) return '-';
  
  switch (format) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value.toString();
    case 'currency':
      return typeof value === 'number' ? 
        new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value) : '-';
    case 'percentage':
      return typeof value === 'number' ? `${value.toFixed(1)}%` : '-';
    default:
      return value.toString();
  }
};

const sortData = (data: CityData[], sortConfig: TableSortConfig): CityData[] => {
  return [...data].sort((a, b) => {
    const aValue = (a as any)[sortConfig.column];
    const bValue = (b as any)[sortConfig.column];
    
    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    
    // Sort based on type
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // String comparison
    const aStr = aValue.toString().toLowerCase();
    const bStr = bValue.toString().toLowerCase();
    
    if (sortConfig.direction === 'asc') {
      return aStr.localeCompare(bStr);
    } else {
      return bStr.localeCompare(aStr);
    }
  });
};

const filterData = (data: CityData[], filters: TableFilterConfig): CityData[] => {
  return data.filter(city => {
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const searchableText = `${city.city_name} ${city.state_name} ${city.state_code}`.toLowerCase();
      if (!searchableText.includes(searchLower)) {
        return false;
      }
    }
    
    // Min user count filter
    if (filters.minUserCount && city.user_count < filters.minUserCount) {
      return false;
    }
    
    // States filter
    if (filters.states?.length && !filters.states.includes(city.state_code)) {
      return false;
    }
    
    return true;
  });
};

// Table header component
const TableHeader: React.FC<{
  column: GeographicTableColumn;
  sortConfig: TableSortConfig | null;
  onSort: (column: string, direction: 'asc' | 'desc') => void;
}> = ({ column, sortConfig, onSort }) => {
  const isSorted = sortConfig?.column === column.key;
  const currentDirection = isSorted ? sortConfig!.direction : null;
  
  const handleSort = () => {
    if (!column.sortable) return;
    
    let newDirection: 'asc' | 'desc' = 'asc';
    if (isSorted && currentDirection === 'asc') {
      newDirection = 'desc';
    }
    
    onSort(column.key, newDirection);
  };

  return (
    <th
      style={{ width: column.width }}
      className={`${column.sortable ? 'cursor-pointer user-select-none' : ''}`}
      onClick={handleSort}
    >
      <div className="d-flex align-items-center justify-content-between">
        <span>{column.label}</span>
        {column.sortable && (
          <div className="ms-1">
            {isSorted ? (
              <i className={`fas fa-sort-${currentDirection === 'asc' ? 'up' : 'down'} text-primary`} aria-hidden="true"></i>
            ) : (
              <i className="fas fa-sort text-muted" aria-hidden="true"></i>
            )}
          </div>
        )}
      </div>
    </th>
  );
};

// Table row component
const TableRow: React.FC<{
  city: CityData;
  columns: GeographicTableColumn[];
  onClick?: (city: CityData) => void;
  index: number;
}> = React.memo(({ city, columns, onClick, index }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(city);
    }
  };

  // Add computed rank
  const cityWithRank = {
    ...city,
    rank: index + 1
  };

  return (
    <tr 
      className={`${onClick ? 'cursor-pointer table-row-hover' : ''}`}
      onClick={handleClick}
    >
      {columns.map(column => (
        <td key={column.key}>
          {column.key === 'city_name' ? (
            <div className="d-flex align-items-center">
              <div>
                <div className="fw-semibold">{city.city_name}</div>
                {city.coordinates && (
                  <small className="text-muted">
                    {city.coordinates.lat.toFixed(2)}, {city.coordinates.lng.toFixed(2)}
                  </small>
                )}
              </div>
            </div>
          ) : column.key === 'state_name' ? (
            <div>
              <div>{city.state_name}</div>
              <small className="text-muted">{city.state_code}</small>
            </div>
          ) : (
            <span className={column.format === 'number' ? 'font-monospace' : ''}>
              {formatValue((cityWithRank as any)[column.key], column.format)}
            </span>
          )}
        </td>
      ))}
    </tr>
  );
});

TableRow.displayName = 'TableRow';

// State filter component
const StateFilter: React.FC<{
  availableStates: string[];
  selectedStates: string[];
  onStateFilterChange: (states: string[]) => void;
}> = ({ availableStates, selectedStates, onStateFilterChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  
  const handleStateToggle = (stateCode: string) => {
    const newSelection = selectedStates.includes(stateCode)
      ? selectedStates.filter(s => s !== stateCode)
      : [...selectedStates, stateCode];
    onStateFilterChange(newSelection);
  };

  const handleSelectAll = () => {
    onStateFilterChange(availableStates);
  };

  const handleClearAll = () => {
    onStateFilterChange([]);
  };

  return (
    <div className="dropdown">
      <button
        className="btn btn-outline-secondary btn-sm dropdown-toggle"
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <i className="fas fa-map-marker-alt me-1" aria-hidden="true"></i>
        States {selectedStates.length > 0 && `(${selectedStates.length})`}
      </button>
      
      {isOpen && (
        <div className="dropdown-menu show p-3" style={{ minWidth: '250px' }}>
          <div className="d-flex justify-content-between mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={handleSelectAll}
            >
              Select All
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleClearAll}
            >
              Clear All
            </button>
          </div>
          
          <div className="border-top pt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {availableStates.map(stateCode => (
              <div key={stateCode} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`state-${stateCode}`}
                  checked={selectedStates.includes(stateCode)}
                  onChange={() => handleStateToggle(stateCode)}
                />
                <label className="form-check-label small" htmlFor={`state-${stateCode}`}>
                  {stateCode}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Pagination component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems: number;
}> = ({ currentPage, totalPages, onPageChange, pageSize, totalItems }) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  
  if (totalPages <= 1) return null;

  return (
    <div className="d-flex justify-content-between align-items-center">
      <div className="text-muted small">
        Showing {startItem}-{endItem} of {totalItems} cities
      </div>
      <nav aria-label="Table pagination">
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
          </li>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            );
          })}
          
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

/**
 * CityPerformanceTable Component
 * 
 * A comprehensive data table for displaying city-level performance metrics
 * with sorting, filtering, pagination, and interactive features.
 * 
 * Features:
 * - Sortable columns with visual indicators
 * - Real-time search filtering with city and state names
 * - State-based filtering with multi-select dropdown
 * - Pagination with customizable page size
 * - Click handlers for drill-down functionality
 * - Loading states with skeleton rows
 * - Responsive design with horizontal scrolling
 * - Geographic coordinates display
 * - Population and performance metrics
 * 
 * Usage:
 * ```tsx
 * <CityPerformanceTable
 *   data={citiesData}
 *   onSort={(column, direction) => handleSort(column, direction)}
 *   onFilter={(filters) => handleFilter(filters)}
 *   onRowClick={(city) => drillDownToCity(city)}
 *   pageSize={25}
 * />
 * ```
 */
const CityPerformanceTable: React.FC<CityPerformanceTableProps> = ({
  data,
  isLoading = false,
  onSort,
  onFilter,
  onRowClick,
  pageSize = 25,
  className = ""
}) => {
  // Local state for table functionality
  const [sortConfig, setSortConfig] = React.useState<TableSortConfig | null>({
    column: 'user_count',
    direction: 'desc'
  });
  const [filters, setFilters] = React.useState<TableFilterConfig>({
    searchTerm: '',
    minUserCount: undefined,
    states: []
  });
  const [currentPage, setCurrentPage] = React.useState(1);

  // Get available states for filtering
  const availableStates = React.useMemo(() => {
    const states = new Set(data.map(city => city.state_code));
    return Array.from(states).sort();
  }, [data]);

  // Process data with sorting and filtering
  const processedData = React.useMemo(() => {
    let result = filterData(data, filters);
    if (sortConfig) {
      result = sortData(result, sortConfig);
    }
    return result;
  }, [data, filters, sortConfig]);

  // Pagination calculations
  const totalPages = Math.ceil(processedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return processedData.slice(startIndex, startIndex + pageSize);
  }, [processedData, currentPage, pageSize]);

  // Handle sorting
  const handleSort = React.useCallback((column: string, direction: 'asc' | 'desc') => {
    const newSortConfig = { column, direction };
    setSortConfig(newSortConfig);
    setCurrentPage(1); // Reset to first page when sorting
    
    if (onSort) {
      onSort(column, direction);
    }
  }, [onSort]);

  // Handle search
  const handleSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    const newFilters = { ...filters, searchTerm };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
    
    if (onFilter) {
      onFilter(newFilters);
    }
  }, [filters, onFilter]);

  // Handle min user count filter
  const handleMinUserCountChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const minUserCount = value ? parseInt(value) : undefined;
    const newFilters = { ...filters, minUserCount };
    setFilters(newFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(newFilters);
    }
  }, [filters, onFilter]);

  // Handle state filter
  const handleStateFilterChange = React.useCallback((states: string[]) => {
    const newFilters = { ...filters, states };
    setFilters(newFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(newFilters);
    }
  }, [filters, onFilter]);

  // Reset filters
  const handleResetFilters = React.useCallback(() => {
    const resetFilters = { searchTerm: '', minUserCount: undefined, states: [] };
    setFilters(resetFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(resetFilters);
    }
  }, [onFilter]);

  // Loading skeleton
  const LoadingSkeleton = () => (
    <tbody>
      {Array.from({ length: pageSize }, (_, index) => (
        <tr key={index}>
          {COLUMNS.map(column => (
            <td key={column.key}>
              <div className="placeholder-glow">
                <div className="placeholder col-8" style={{ height: '1rem' }}></div>
              </div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className={`city-performance-table ${className}`.trim()}>
      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-4">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search" aria-hidden="true"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search cities and states..."
              value={filters.searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="col-md-2">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-users" aria-hidden="true"></i>
            </span>
            <input
              type="number"
              className="form-control"
              placeholder="Min users"
              value={filters.minUserCount || ''}
              onChange={handleMinUserCountChange}
              min="0"
            />
          </div>
        </div>
        <div className="col-md-3">
          <StateFilter
            availableStates={availableStates}
            selectedStates={filters.states || []}
            onStateFilterChange={handleStateFilterChange}
          />
        </div>
        <div className="col-md-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleResetFilters}
              disabled={!filters.searchTerm && !filters.minUserCount && !filters.states?.length}
            >
              <i className="fas fa-times me-1" aria-hidden="true"></i>
              Clear
            </button>
            <div className="text-muted small align-self-center">
              {processedData.length} cities
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover table-striped">
          <thead className="table-dark">
            <tr>
              {COLUMNS.map(column => (
                <TableHeader
                  key={column.key}
                  column={column}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          
          {isLoading ? (
            <LoadingSkeleton />
          ) : processedData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={COLUMNS.length} className="text-center py-4 text-muted">
                  <i className="fas fa-search fa-2x mb-2" aria-hidden="true"></i>
                  <p className="mb-0">
                    {data.length === 0 
                      ? "No city data available" 
                      : "No cities match your search criteria"
                    }
                  </p>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {paginatedData.map((city, index) => (
                <TableRow
                  key={`${city.city_name}-${city.state_code}`}
                  city={city}
                  columns={COLUMNS}
                  onClick={onRowClick}
                  index={(currentPage - 1) * pageSize + index}
                />
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && processedData.length > 0 && (
        <div className="mt-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            totalItems={processedData.length}
          />
        </div>
      )}

      {/* Custom CSS for hover effects */}
      <style>{`
        .cursor-pointer {
          cursor: pointer !important;
        }
        
        .table-row-hover:hover {
          background-color: rgba(13, 110, 253, 0.1) !important;
        }
        
        .user-select-none {
          user-select: none;
        }
        
        th.cursor-pointer:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        .dropdown-menu.show {
          display: block;
          position: absolute;
          z-index: 1000;
        }
      `}</style>
    </div>
  );
};

CityPerformanceTable.displayName = 'CityPerformanceTable';

export default CityPerformanceTable;